<template>
  <section class="sidebar-card markers-card">
    <header class="markers-head">
      <div class="head-row">
        <h3 class="card-title">Markers</h3>
        <span class="result-count">{{ filteredMarkers.length }}</span>
      </div>

      <div class="tools-row">
        <div class="search-wrap">
          <v-icon size="16" class="search-icon">mdi-magnify</v-icon>
          <input v-model="searchQuery" type="text" class="search-input" placeholder="Search # or area" />
        </div>
        <button type="button" class="tool-btn" :class="{ active: filtersOpen }" title="Filters"
          @click="filtersOpen = !filtersOpen">
          <v-icon size="18">mdi-filter-variant</v-icon>
        </button>
        <div class="view-toggle">
          <button type="button" class="tool-btn" :class="{ active: view === 'list' }" title="List view"
            @click="view = 'list'">
            <v-icon size="18">mdi-format-list-bulleted</v-icon>
          </button>
          <button type="button" class="tool-btn" :class="{ active: view === 'grid' }" title="Grid view"
            @click="view = 'grid'">
            <v-icon size="18">mdi-view-grid-outline</v-icon>
          </button>
        </div>
      </div>

      <div v-if="filtersOpen" class="filters">
        <v-select v-model="selectedTypes" :items="markerTypes" label="Marker Type" multiple variant="outlined"
          density="compact" hide-details chips class="filter-input" />
        <v-select v-model="selectedAreas" :items="inspectionAreas" label="Inspection Areas" multiple item-title="name"
          item-value="id" variant="outlined" density="compact" hide-details chips class="filter-input" />
        <v-select v-model="selectedStatuses" :items="markerStatuses" label="Approval Status" multiple variant="outlined"
          density="compact" hide-details chips class="filter-input" />
      </div>
    </header>

    <div class="results-list" :class="`view-${view}`">
      <template v-if="filteredMarkers.length > 0">
        <div v-for="marker in filteredMarkers" :key="marker.id" class="result-item">
          <MarkerListItem :marker="marker" :scanName="scanNames[marker.scanId]" :areaName="areaNameFor(marker)"
            :buttonEnabled="modelLoaded" :layout="view" :selected="marker.id === selectedId"
            @onLocateMarker="locateMarkerGroup" />
        </div>
      </template>
      <div v-else class="results-empty">
        <v-icon size="32" color="#4a5a60">mdi-magnify</v-icon>
        <span>No matching markers</span>
      </div>
    </div>
  </section>
</template>

<script>
import AnomalyMarker from '@/domain/marker/AnomalyMarker';
import InspectionAreaMarker from '@/domain/marker/InspectionAreaMarker';
import MarkerListItem from './list-items/MarkerListItem.vue';

export default {
  name: 'MarkerGroupSearchComponent',
  props: {
    markers: { type: Array, required: true },
    asset: { type: Object, required: true },
    modelLoaded: { type: Boolean, default: false },
    scanNames: { type: Object, default: () => ({}) },
    selectedId: { type: [String, Number], default: null },
  },
  components: { MarkerListItem },
  data() {
    return {
      searchQuery: '',
      filtersOpen: false,
      view: 'list',
      selectedTypes: [],
      generatedTypes: [true, false],
      selectedAreas: [],
      selectedStatuses: [],
    };
  },
  emits: ['navigateToMarker'],
  computed: {
    markerTypes() {
      return [...new Set(this.markers.filter(m => m instanceof AnomalyMarker).map(m => m.label))];
    },
    inspectionAreas() {
      return [...new Set(this.markers.filter(m => m instanceof InspectionAreaMarker).map(m => ({ name: m.name, id: m.id })))];
    },
    // id -> name lookup so each row can show its inspection area.
    areaNameById() {
      const map = {};
      for (const m of this.markers) {
        if (m instanceof InspectionAreaMarker) map[m.id] = m.name;
      }
      return map;
    },
    markerStatuses() {
      return [...new Set(this.markers.filter(m => m instanceof AnomalyMarker).map(m => m.status))];
    },
    filteredMarkers() {
      let filtered = this.markers;
      if (this.selectedTypes.length > 0) {
        filtered = filtered.filter(m => this.selectedTypes.includes(m.label));
      }
      if (this.generatedTypes.length > 0) {
        filtered = filtered.filter(m => this.generatedTypes.includes(m.isAIGenerated));
      }
      if (this.selectedStatuses.length > 0) {
        filtered = filtered.filter(m => this.selectedStatuses.includes(m.status));
      }
      if (this.selectedAreas.length > 0) {
        filtered = filtered.filter(m => this.selectedAreas.includes(m.inspectionAreaId));
      }
      const q = this.searchQuery.trim().toLowerCase();
      if (q) {
        filtered = filtered.filter(m => {
          const name = (m.name || m.label || '').toLowerCase();
          const area = (this.areaNameById[m.inspectionAreaId] || '').toLowerCase();
          const id = String(m.id || '').toLowerCase();
          return name.includes(q) || area.includes(q) || id.includes(q);
        });
      }
      return filtered;
    },
  },
  methods: {
    areaNameFor(marker) {
      return this.areaNameById[marker.inspectionAreaId] || '';
    },
    locateMarkerGroup(marker) {
      this.$emit('navigateToMarker', marker);
    },
  }
};
</script>

<style scoped>
.markers-card {
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  color: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

.markers-head {
  padding: clamp(10px, calc(6px + 0.3vw), 14px);
  display: flex;
  flex-direction: column;
  gap: 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.head-row { display: flex; align-items: center; gap: 8px; }
.card-title { font-size: 1rem; font-weight: 700; margin: 0; color: #fff; }
.result-count {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 26px; height: 22px; padding: 0 8px; border-radius: 999px;
  background: rgba(100, 255, 218, 0.1); border: 1px solid rgba(100, 255, 218, 0.25);
  color: var(--duallo-accent); font-size: 0.75rem; font-weight: 700; font-variant-numeric: tabular-nums;
}

/* Search + filter + view toggle */
.tools-row { display: flex; align-items: center; gap: 8px; }
.search-wrap {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
}
.search-icon { position: absolute; left: 9px; color: #7f939d; pointer-events: none; }
.search-input {
  width: 100%;
  padding: 8px 10px 8px 30px;
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #e6edf0;
  font: inherit;
  font-size: 0.85rem;
  outline: none;
}
.search-input::placeholder { color: #7f939d; }
.search-input:focus { border-color: rgba(100, 255, 218, 0.4); background: rgba(255, 255, 255, 0.06); }

.view-toggle { display: inline-flex; gap: 4px; flex: 0 0 auto; }
.tool-btn {
  flex: 0 0 auto;
  display: inline-flex; align-items: center; justify-content: center;
  width: 34px; height: 34px; border-radius: 9px;
  background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.1);
  color: #b0bec5; cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}
.tool-btn:hover { background: rgba(255, 255, 255, 0.08); color: #fff; }
.tool-btn.active { background: rgba(100, 255, 218, 0.12); border-color: rgba(100, 255, 218, 0.35); color: var(--duallo-accent); }

.filters { display: flex; flex-direction: column; gap: 10px; padding-top: 2px; }
.filter-input :deep(.v-field) { border-radius: 10px; background: rgba(255, 255, 255, 0.03); }

/* Results list — themed scrollbar matching the asset list, with breathing room from the bar. */
.results-list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 10px 6px 12px 10px;
  scrollbar-width: thin;
  scrollbar-color: #3a4a52 transparent;
}
.results-list::-webkit-scrollbar { width: 6px; }
.results-list::-webkit-scrollbar-track { background: transparent; }
.results-list::-webkit-scrollbar-thumb { background: #3a4a52; border-radius: 3px; }
.results-list::-webkit-scrollbar-thumb:hover { background: var(--duallo-accent); }

.results-list.view-list { display: flex; flex-direction: column; gap: 8px; }
/* grid-auto-rows: max-content stops the rows stretching to fill the container (which would
   leave nothing to scroll); align-content: start packs them at the top. */
.results-list.view-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); gap: 8px; align-content: start; grid-auto-rows: max-content; }

.result-item {
  flex: 0 0 auto;   /* never let the flex column compress rows into slivers */
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 10px;
  overflow: hidden;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.result-item:hover { background: rgba(100, 255, 218, 0.05); border-color: rgba(100, 255, 218, 0.15); }
/* Selected marker: teal ring (mirrors the 3D selection). */
.result-item:has(.is-selected) { border-color: rgba(100, 255, 218, 0.6); box-shadow: 0 0 0 1px rgba(100, 255, 218, 0.35) inset; }

.results-empty {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 6px; padding: 28px 8px; color: #8aa0aa; font-size: 0.85rem;
  grid-column: 1 / -1;
}
</style>
